import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Button,
  Grid,
  Typography,
  IconButton,
  InputAdornment,
  Container,
  Grid2,
  Box,
  Avatar,
  Card,
  CardContent,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import sendIcon from "../../../assets/images/sendIcon.png";
import { toast } from "react-toastify";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import {
  createProject,
  registerUser,
  verifyOtp,
  resendOtp,
} from "../../../apis/apisList/userApi";
import { useNavigate } from "react-router-dom";
import { useApp } from "../../../Context/AppContext";
import fileUpload from "../../../lib/fileUpload";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import CheckIcon from "@mui/icons-material/Check";

const PasswordField = (props) => {
  const [show, setShow] = useState(false);

  const toggleVisibility = () => {
    setShow((prev) => !prev);
  };

  return (
    <TextField
      {...props}
      type={show ? "text" : "password"}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <IconButton onClick={toggleVisibility} edge="end">
              {show ? <Visibility /> : <VisibilityOff />}
            </IconButton>
          </InputAdornment>
        ),
      }}
    />
  );
};

const EditableProfileCard = ({ formData, setFormData }) => {
  const [isEditing, setIsEditing] = useState(false);

  const handleChange = (field) => (e) => {
    setFormData((prev) => ({
      ...prev,
      [field]: e.target.value,
    }));
  };

  const toggleEdit = () => {
    setIsEditing((prev) => !prev);
  };

  const renderField = (label, field) => (
    <Box display="flex" alignItems="center" mb={2}>
      <Typography
        variant="subtitle2"
        color="text.secondary"
        sx={{ minWidth: 60 }}
      >
        {label}
      </Typography>
      <TextField
        variant="standard"
        fullWidth
        value={formData[field]}
        onChange={handleChange(field)}
        disabled={!isEditing}
        InputProps={{ disableUnderline: !isEditing }}
        sx={{ ml: 2 }}
      />
    </Box>
  );

  return (
    <Card
      sx={{
        width: 420,
        p: 2,
        position: "relative",
        mt: 1,
        mb: 2,
        flex: 1,
      }}
    >
      <CardContent>
        {renderField("Name", "fullName")}
        {renderField("Email", "email")}
        {renderField("Phone", "phone")}
      </CardContent>

      <IconButton
        onClick={toggleEdit}
        sx={{ position: "absolute", top: 0, right: 3 }}
        // color="primary"
      >
        {isEditing ? <CheckIcon /> : <BorderColorIcon />}
      </IconButton>
    </Card>
  );
};

const QuotationDialog = ({
  open,
  onClose,
  projectName,
  address,
  handleRequestQuote = null,
}) => {
  const { user, setShouldWarn } = useApp();
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    date: "",
    message: "",
    profile_image: "",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showVerification, setShowVerification] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [verificationEmail, setVerificationEmail] = useState("");
  const [otp, setOtp] = useState("");
  const navigate = useNavigate();
  const { setProjectData, user: userData, login } = useApp();
  const [profileImage, setProfileImage] = useState("");
  useEffect(() => {
    // let user = localStorage.getItem("user");
    if (userData) {
      setFormData({
        ...formData,
        fullName: userData.fullName,
        email: userData.email,
        phone: userData.phone,
      });
    }
    setShouldWarn(false);
  }, [userData]);

  const userToken = userData && localStorage.getItem("jwtToken");

  const validateForm = () => {
    let tempErrors = {};

    if (!formData?.fullName?.trim())
      tempErrors.fullName = "Full name is required";
    if (!formData?.email?.trim()) tempErrors.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(formData.email))
      tempErrors.email = "Invalid email format";

    if (formData?.phone && !/^\d{9,12}$/.test(formData?.phone))
      tempErrors.phone = "Invalid phone number";
    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      if (handleRequestQuote && typeof handleRequestQuote === "function") {
        const quoteData = {
          about: formData.message,
          date: formData.date || "",
          contact: {
            fullName: formData.fullName,
            email: formData.email,
            phone: formData.phone || "",
          },
        };
        let resData = await handleRequestQuote(quoteData);
        if (resData) {
          setLoading(false);
          onClose();
        }
        return;
      }
      if (!userToken) {
        registerUser({
          fullName: formData.fullName,
          email: formData.email,
          phone: formData.phone || "",
          pendingPasswordSetup: true,
        })
          .then(async (data) => {
            await submitQuotation();
          })
          .catch(async (error) => {
            if (error?.response?.data.message == "Email already in use") {
              console.log(error?.response?.data.message);
              await submitQuotation();
            }
          });
      } else {
        await submitQuotation();
      }
    } catch (error) {
      setLoading(false);
      toast.error(error.response?.data?.message || "An error occurred");
    }
  };

  const handleVerification = async (otp) => {
    if (!verificationEmail) return;
    try {
      const response = await verifyOtp({ otp, email: verificationEmail });
      login(response);
      await submitQuotation();
      setShowVerification(false);
    } catch (error) {
      toast.error(error.response?.data?.message || "Verification failed");
    }
  };

  const handleResend = async () => {
    if (!verificationEmail) return;
    try {
      await resendOtp({ email: verificationEmail });
      toast.success("OTP has been sent to your email");
    } catch (error) {
      toast.error("Error sending otp");
    }
  };

  const submitQuotation = async () => {
    try {
      const projectData = localStorage.getItem("projectData");
      if (!projectData) {
        toast.error("No project data found. Please select a plan first.");
        return;
      }

      const { data, address: pAddress } = JSON.parse(projectData);
      const price = data.reduce((acc, curr) => acc + curr.result.price, 0);

      const quoteData = {
        name: projectName || "untitled",
        about: formData.message,
        date: formData.date || "",
        contact: {
          fullName: formData.fullName,
          email: formData.email,
          phone: formData.phone || "",
        },
        plans: JSON.stringify(data),
        price,
        address: address || pAddress,
        isRegistered: false,
      };
      const userToken = JSON.parse(localStorage.getItem("user")) ?? null;
      if (userToken && user._id) {
        quoteData.user = user._id;
        quoteData.isRegistered = true;
        quoteData.pendingPasswordSetup = false;
      } else {
        quoteData.pendingPasswordSetup = true;
      }
      const res = await createProject(quoteData);
      setLoading(false);
      toast.success(
        "Thank you, we’ll review and be in touch ASAP with your quote."
      );
      setFormData({
        fullName: "",
        email: "",
        phone: "",
        date: "",
        message: "",
      });
      setShouldWarn(false);
      const link = res?.data?.userLink;
      localStorage.removeItem("projectData");
      localStorage.removeItem("edit_project");
      localStorage.removeItem("edit_id");
      localStorage.removeItem("project_add_more");
      localStorage.removeItem("edit_project_id");
      setProjectData([]);
      const redirect = userToken ? "/dashboard" : "/";
      navigate(redirect);
      // if (userToken) {
      //   navigate("/dashboard");
      // } else {
      //   window.location.assign(link);
      // }

      onClose();
    } catch (error) {
      setLoading(false);
      toast.error(
        "Oops. Something went wrong. Please screenshot this screen and email (please note property address) to flights@flyover.io and we’ll send you a quote, or call us at (608) 571 7992",
        {
          autoClose: false,
        }
      );
      console.error("Error during submit quote", error);
    }
  };

  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setFormData({
        ...formData,
        profile_image: file,
      });
      const reader = new FileReader();
      reader.onload = () => {
        setProfileImage(reader.result); // Update the profile image with the uploaded image
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle
          sx={{
            backgroundColor: "#1D1D29",
            color: "white",
            padding: "23px",
            fontWeight: "bold",
          }}
        >
          {userToken
            ? "Please Verify Your Contact Information."
            : "Please provide your contact information so we can send you a quote."}

          <IconButton
            sx={{ position: "absolute", right: 8, top: 8, color: "white" }}
            onClick={onClose}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ padding: "24px" }}>
          {/* <Typography sx={{ mb: 2, color: "#666", paddingY: 3 }}>
            Our friendly team is ready to answer your questions. Call or please
            fill out the information below for your no-obligation quote.
          </Typography> */}
          <Typography sx={{ fontWeight: "bold", mb: 2, mt: 2 }}>
            You can also call us at (608) 571 7992 to discuss your project
          </Typography>

          <form onSubmit={handleSubmit} noValidate>
            {userToken ? (
              <EditableProfileCard
                formData={formData}
                setFormData={setFormData}
              />
            ) : (
              <Box
                sx={{ display: "flex", gap: 2, flexDirection: "column", mb: 3 }}
              >
                {/* Full Name and email */}
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Full Name"
                      variant="outlined"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleChange}
                      error={!!errors.fullName}
                      helperText={errors.fullName}
                    />
                  </Grid>

                  {/* Email Address */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Email Address"
                      variant="outlined"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      error={!!errors.email}
                      helperText={errors.email}
                    />
                  </Grid>
                </Grid>

                {/* phone and Shoot Date  */}
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Phone Number"
                      variant="outlined"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      error={!!errors.phone}
                      helperText={errors.phone}
                      type="number"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Choose your preferred shoot date"
                      variant="outlined"
                      name="date"
                      type="date"
                      InputLabelProps={{ shrink: true }}
                      value={formData.date}
                      onChange={handleChange}
                      error={!!errors.date}
                      helperText={errors.date}
                    />
                  </Grid>
                </Grid>
              </Box>
            )}

            <Grid container spacing={2}>
              {/* Project Details */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Please tell us anything else you think we should know about your project."
                  variant="outlined"
                  name="message"
                  multiline
                  rows={4}
                  value={formData.message}
                  onChange={handleChange}
                />
              </Grid>

              {/* Submit Button */}
              <Grid item xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<img src={sendIcon} />}
                  sx={{
                    backgroundColor: "#065F9C",
                    color: "white",
                    padding: "12px",
                    "&:hover": {
                      backgroundColor: "#075F9C",
                    },
                    paddingY: 1,
                    borderRadius: 2,
                  }}
                  disabled={loading}
                >
                  {loading ? "Submitting..." : "Submit"}
                </Button>
              </Grid>
            </Grid>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog
        open={showVerification}
        onClose={() => {
          setShowVerification(false);
          setLoading(false);
        }}
      >
        <DialogContent>
          <Typography variant="h6" gutterBottom>
            Email Verification
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Please enter the verification code sent to {verificationEmail}
          </Typography>
          <TextField
            fullWidth
            label="Verification Code"
            type="number"
            value={otp}
            onChange={(e) => {
              const value = e.target.value;
              if (value.length <= 6 && /^\d*$/.test(value)) {
                setOtp(value);
              }
            }}
            inputProps={{ maxLength: 6 }} // Ensures no more than 6 characters
          />
          <Button
            fullWidth
            onClick={() => handleVerification(otp)}
            sx={{ mt: 2, color: "#fff" }}
            variant="contained"
          >
            Submit
          </Button>
          <Button
            fullWidth
            onClick={handleResend}
            sx={{ mt: 2, color: "#065F9C" }}
          >
            Resend Code
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default QuotationDialog;
